import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  Button,
} from '@mui/material';
import {
  People,
  Accessible,
  LocationOn,
  Search,
} from '@mui/icons-material';
import { useRegionContext } from '../../region/RegionContext';
import { queryAllRegions } from '../../utils/queryAllRegions';
import TabDescription from './TabDescription';

const PAGE_SIZE = 25;

const UsersTab = () => {
  const { db, isGlobal, allRegionDbs } = useRegionContext();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    loadUsers();
  }, [db, isGlobal, allRegionDbs]);

  const loadUsers = async () => {
    try {
      setLoading(true);

      let data;
      if (isGlobal && allRegionDbs) {
        data = await queryAllRegions(allRegionDbs, null, (regionDb) =>
          regionDb.collection('Users').limit(500)
        );
      } else {
        const snapshot = await db.collection('Users').limit(500).get();
        data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
      }

      setUsers(data);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (user.id || '').toLowerCase().includes(q) ||
      (user.userState || '').toLowerCase().includes(q) ||
      (user.userSuburb || '').toLowerCase().includes(q) ||
      (user.userName || '').toLowerCase().includes(q) ||
      (user.userEmail || '').toLowerCase().includes(q)
    );
  });

  const accessibilityCount = users.filter(u => u.userAccessibility).length;
  const statesBreakdown = {};
  users.forEach(u => {
    const state = u.userState || 'Unknown';
    statesBreakdown[state] = (statesBreakdown[state] || 0) + 1;
  });

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading users...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <TabDescription>
        All registered Piatto users from the Users Firestore collection. Summary cards show total counts,
        accessibility users, and geographic distribution by state. Use the search box to find specific users
        by name, email, or location. Monitor user growth and accessibility adoption to guide feature
        development and outreach.
      </TabDescription>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <People sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">Total Users</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>{users.length}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Accessible sx={{ fontSize: 40, color: 'info.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">Accessibility Users</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>{accessibilityCount}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LocationOn sx={{ fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">States Represented</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>{Object.keys(statesBreakdown).length}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {Object.entries(statesBreakdown)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 4)
                  .map(([state, count]) => (
                    <Chip key={state} label={`${state}: ${count}`} size="small" />
                  ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search by user ID, name, email, state, or suburb..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Users ({filteredUsers.length})
          </Typography>

          {filteredUsers.length === 0 ? (
            <Alert severity="info">No users found</Alert>
          ) : (
            <>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
              <Button variant="outlined" size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                Previous
              </Button>
              <Typography variant="body2">
                Page {page + 1} of {Math.ceil(filteredUsers.length / PAGE_SIZE)} ({filteredUsers.length} total)
              </Typography>
              <Button variant="outlined" size="small" disabled={(page + 1) * PAGE_SIZE >= filteredUsers.length} onClick={() => setPage(p => p + 1)}>
                Next
              </Button>
            </Box>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table aria-label="Registered users">
                <TableHead>
                  <TableRow>
                    <TableCell scope="col"><strong>User ID</strong></TableCell>
                    <TableCell scope="col"><strong>Name</strong></TableCell>
                    <TableCell scope="col"><strong>Email</strong></TableCell>
                    <TableCell scope="col"><strong>State</strong></TableCell>
                    <TableCell scope="col"><strong>Suburb</strong></TableCell>
                    <TableCell scope="col"><strong>Accessibility</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {user.id.substring(0, 12)}...
                        </Typography>
                      </TableCell>
                      <TableCell>{user.userName || '—'}</TableCell>
                      <TableCell>{user.userEmail || '—'}</TableCell>
                      <TableCell>{user.userState || '—'}</TableCell>
                      <TableCell>{user.userSuburb || '—'}</TableCell>
                      <TableCell>
                        {user.userAccessibility ? (
                          <Chip icon={<Accessible />} label="Yes" size="small" color="info" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default UsersTab;
